import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface MetricsSummary {
  revenue: number;
  orders: number;
  visits: number;
  clicks: number;
  pageViews: number;
  units: number;
  conversion: number;
}

export interface MetricsComparison {
  revenue: { current: number; previous: number; deltaPercent: number };
  orders: { current: number; previous: number; deltaPercent: number };
  visits: { current: number; previous: number; deltaPercent: number };
  conversion: { current: number; previous: number; deltaPercent: number };
}

export interface MetricsSeries {
  date: string;
  revenue: number;
  orders: number;
  visits: number;
  clicks: number;
}

export interface SellerFunnelStage {
  id: string;
  label: string;
  value: string | null;
  secondary: string | null;
  pct: number | null;
}

export interface SellerFunnel {
  totalConversion: {
    current: number | null;
    previous: number | null;
    deltaPoints: number | null;
    state: string | null; // 'up' | 'down'
  };
  stages: SellerFunnelStage[];
  comparisonLabel: string | null;
}

export interface SellerOverviewData {
  summary: MetricsSummary;
  comparison: MetricsComparison;
  series: MetricsSeries[];
  periodDays: number;
  hasData: boolean;
  funnel?: SellerFunnel | null;
}

export function useSellerMetrics(connectionId?: string | null, days = 7) {
  const { user } = useAuth();
  const [data, setData] = useState<SellerOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!user?.tenantId) {
      setData(null);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
      const params = new URLSearchParams({ days: String(days) });
      if (connectionId) params.set('connectionId', connectionId);

      const res = await fetch(`/api/seller/metrics/overview?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch metrics');
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar métricas');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, connectionId, days]);

  useEffect(() => {
    setLoading(true);
    fetchOverview();
  }, [fetchOverview]);

  const syncMetrics = useCallback(async (connId?: string, marketplace?: string, targets?: Array<{ connectionId: string; marketplace: string }>) => {
    const targetConnection = connId || connectionId;
    if ((!targetConnection && (!targets || targets.length === 0)) || !user?.tenantId) return;

    setSyncing(true);
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const syncPeriod = async (target: { connectionId: string; marketplace: string }, periodDays: number) => {
      const res = await fetch('/api/seller/metrics/sync', {
        method: 'POST',
        headers,
        body: JSON.stringify({ connectionId: target.connectionId, periodDays, marketplace: target.marketplace || 'shopee' }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Sync failed');
    };

    try {
      // Sincroniza os dois períodos (7d e 30d) numa tacada só.
      const syncTargets = targets && targets.length > 0
        ? targets
        : [{ connectionId: targetConnection as string, marketplace: marketplace || 'shopee' }];

      for (const target of syncTargets) {
        await syncPeriod(target, 7);
        await syncPeriod(target, 30);
      }
      // Refetch overview after sync
      await fetchOverview();
    } catch (err: any) {
      setError(err.message || 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  }, [connectionId, user?.tenantId, fetchOverview]);

  return { data, loading, syncing, error, syncMetrics, refetch: fetchOverview };
}
